import { componentText, HostAriaLabelMixin, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { show } from '@schmancy/overlay/overlay.service'
import { SchmancyOverlay } from '@schmancy/overlay/overlay.component'
import { inExemptLayer } from '@schmancy/overlay/overlay.stack'
import type { ResolvedShowOptions } from '@schmancy/overlay/overlay.types'
import { css, html } from 'lit'
import { state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { when } from 'lit/directives/when.js'
import { defer, EMPTY, exhaustMap, finalize, merge, Subject, switchMap, takeUntil, timer } from 'rxjs'
import '../button/icon-button'
import '../file-item'

/**
 * @element schmancy-file-stack
 * @slot - Existing file cards or HTMLElement card hosts, with their own previews and click handlers.
 *
 * Any number of cards occupies one card's box. One file activates directly;
 * multiple files disclose the original cards through the shared overlay.
 */
@schmancyElement('schmancy-file-stack')
export class SchmancyFileStack extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
				position: relative;
				width: 100%;
				aspect-ratio: 4 / 3;
			}
		`,
	]

	/** Observable presentation state; opening is a user action, not a property command. */
	@state() expanded = false
	@state() private files: HTMLElement[] = []
	private readonly opening$ = new Subject<'hover' | 'activation'>()
	private readonly closing$ = new Subject<void>()
	private readonly hovering$ = new Subject<boolean>()
	private readonly pinning$ = new Subject<void>()
	private readonly panel = createRef<HTMLElement>()
	private readonly original = new WeakMap<HTMLElement, { slot: string; inert: boolean }>()

	override connectedCallback(): void {
		super.connectedCallback()
		this.opening$
			.pipe(
				exhaustMap(mode =>
					defer(() => {
						if (this.files.length < 2) return EMPTY
						const captured = this.files.map(item => {
							const placeholder = document.createComment('file-stack position')
							item.before(placeholder)
							item.inert = this.original.get(item)?.inert ?? false
							return { item, placeholder }
						})
						const options: ResolvedShowOptions = {
							ariaLabel: this.ariaLabel || componentText('fileStack.files', { count: captured.length }),
							historyStrategy: 'silent',
						}
						// Hover carries no ambient activation gesture. The library resolves
						// its own anchor rather than inheriting an unrelated recent click.
						if (mode === 'hover') {
							options.anchor = this
							options.sticky = true
						}
						this.expanded = true
						return show(
							() => html`
								<div
									${ref(this.panel)}
									class="grid w-lg max-w-full gap-2 p-2"
									?data-overlay-exempt=${inExemptLayer(this)}
									@pointerenter=${() => this.hovering$.next(true)}
									@pointerleave=${() => this.hovering$.next(false)}
									@pointerdown=${this.pin}
								>
									<schmancy-icon-button
										class="justify-self-end"
										icon="close"
										aria-label=${componentText('fileStack.close')}
										@click=${this.close}
									></schmancy-icon-button>
									<div class="grid grid-cols-2 gap-2">
										${repeat(
											captured,
											entry => entry.item,
											// The overlay mounts outside this component's styled shadow root.
											entry => html` <div class="min-w-0" style="aspect-ratio: 4 / 3">${entry.item}</div> `,
										)}
									</div>
								</div>
							`,
							options,
						).pipe(
							takeUntil(
								merge(
									this.closing$,
									mode === 'hover'
										? this.hovering$.pipe(
												switchMap(hovering => (hovering ? EMPTY : timer(200))),
												takeUntil(this.pinning$),
											)
										: EMPTY,
								),
							),
							finalize(() => {
								for (const { item, placeholder } of captured) {
									if (this.isConnected && !item.parentNode) {
										placeholder.remove()
										continue
									}
									if (placeholder.parentNode) placeholder.replaceWith(item)
									else item.remove()
								}
								this.expanded = false
								if (this.isConnected) this.collectFiles()
							}),
						)
					}),
				),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	/** Dismisses this stack's expansion without dismissing any document opened from it. */
	close = (): void => this.closing$.next()

	override disconnectedCallback(): void {
		super.disconnectedCallback()
		for (const item of this.children) {
			if (!(item instanceof HTMLElement)) continue
			const original = this.original.get(item)
			if (!original) continue
			item.slot = original.slot
			item.inert = original.inert
		}
	}

	private pin = (): void => {
		const root = this.panel.value?.getRootNode()
		if (root instanceof ShadowRoot && root.host instanceof SchmancyOverlay) root.host.sticky = false
		this.pinning$.next()
	}

	private collectFiles = (): void => {
		if (this.expanded || !this.isConnected) return
		this.files = Array.from(this.children).filter((item): item is HTMLElement => item instanceof HTMLElement)
		this.files.forEach((item, index) => {
			if (!this.original.has(item)) this.original.set(item, { slot: item.slot, inert: item.inert })
			item.slot = `file-stack-${index}`
			item.inert = this.files.length > 1 || (this.original.get(item)?.inert ?? false)
		})
	}

	override render() {
		return html`
			<div class="relative h-full w-full">
				${repeat(
					this.files,
					item => item,
					(_item, index) => html`
						<div
							class=${this.classMap({
								absolute: true,
								'inset-0 z-2': index === 0 && this.files.length === 1,
								'top-0 left-0 right-2 bottom-2 z-2': index === 0 && this.files.length > 1,
								'inset-1 z-1': index === 1,
								'top-2 left-2 bottom-0 right-0 z-0': index === 2,
								hidden: index > 2,
							})}
						>
						<slot name=${`file-stack-${index}`} @slotchange=${this.collectFiles}></slot>
						</div>
					`,
				)}
				${when(
					this.files.length > 1,
					() => html`
						<button
							type="button"
							class="absolute inset-0 z-3 cursor-pointer rounded-xl bg-transparent hover:ring-2 hover:ring-primary-default/30 active:ring-2 active:ring-primary-default/50 focus-visible:outline-2 focus-visible:outline-primary-default"
							aria-label=${this.ariaLabel || componentText('fileStack.open', { count: this.files.length })}
							aria-expanded=${this.expanded}
							aria-haspopup="true"
							@click=${(event: Event) => {
								event.stopPropagation()
								if (this.expanded) this.pin()
								else this.opening$.next('activation')
							}}
							@pointerenter=${(event: PointerEvent) => {
								if (event.pointerType !== 'mouse') return
								this.hovering$.next(true)
								this.opening$.next('hover')
							}}
							@pointerleave=${() => this.hovering$.next(false)}
						>
							<span class="absolute bottom-1 right-1 text-sm font-medium text-surface-on" aria-hidden="true">${this.files.length}</span>
						</button>
					`,
				)}
				<div hidden><slot @slotchange=${this.collectFiles}></slot></div>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-file-stack': SchmancyFileStack
	}
}
